import { PreferenceFragment } from '@graphqlTypes/sitecore';
import { DeepReadonly } from '@src/utils/type-utils';
import Script from 'next/script';
import { createContext, ReactNode, useContext, useState } from 'react';
import {
    AdobeDataLayer,
    InteractionInfo,
    NonPageViewParam,
    PageViewTrackedParam,
    PreviewInteractionInfo,
    TrackedArticleInfo,
} from './global';
import { TrackingEvent } from './types';
import { useRouter } from 'next/router';

const AdobeDataLayerContext = createContext<AdobeDataLayer | null>(null);

export function AdobeDataLayerProvider(props: { children?: ReactNode }) {
    const [dataLayer, setDataLayer] = useState<AdobeDataLayer | null>(null);
    const router = useRouter();
    // const isPreviewPage = router.asPath.startsWith('/share/');
    const isPreviewPage = /\/share\//.test(router.asPath);

    // console.log('***************isPreviewPage***************', isPreviewPage);
    // console.log('***************router.asPath***************', router.asPath);
    // console.log('***************window.location.pathname***************', window.location.pathname);

    return (
        <>
            <Script
                src={
                    isPreviewPage
                        ? process.env.NEXT_PUBLIC_PREVIEW_ADOBE_LAUNCH_SRC
                        : process.env.NEXT_PUBLIC_ADOBE_LAUNCH_SCRIPT_SRC
                }
                type='text/javascript'
                onLoad={() => {
                    // if (window.adobeDataLayer) {
                    //     setDataLayer(window.adobeDataLayer);
                    // } else {
                    //     console.error('adobeDataLayer not defined');
                    // }
                    const checkAdobeDataLayer = setInterval(() => {
                        if (window.adobeDataLayer) {
                            clearInterval(checkAdobeDataLayer);
                            setDataLayer(window.adobeDataLayer);
                        }
                    }, 100);

                    setTimeout(() => clearInterval(checkAdobeDataLayer), 5000);
                }}
            />
            <AdobeDataLayerContext.Provider value={dataLayer}>
                {props.children}
            </AdobeDataLayerContext.Provider>
        </>
    );
}

export function useAdobeDataLayer() {
    const dataLayer = useContext(AdobeDataLayerContext);
    if (dataLayer === undefined) {
        throw new Error('You have forgot to use AdobeDataLayerProvider');
    }
    return dataLayer;
}

export function useNonPageTracker() {
    const dataLayer = useAdobeDataLayer();
    if (!dataLayer) {
        return null;
    } else {
        return nonPageTracker;
    }
}

export function usePreferenceTracker() {
    const nonPageTracker = useNonPageTracker();
    if (!nonPageTracker) {
        return {
            trackPreferenceStep1: null,
            trackPreferenceStep2: null,
            trackPreferenceStep3: null,
        };
    } else {
        return {
            trackPreferenceStep1,
            trackPreferenceStep2,
            trackPreferenceStep3,
        };
    }
}

/** 触发式下发
 * @param interaction - A partial representation of InteractionInfo.
 * This allows for flexibility in which properties are included:
 * - `eventID`: The ID of the event.
 * - `eventType`: Type of the event.
 * - `eventValue`: Value associated with the event.
 * - `preference`: User preference (1: raceday, 2: simulcast, 3: dining).
 * - `videoURL`: URL for a related video.
 * - `raceID`: ID of the race.
 * - `kolName`: Name of the KOL (Key Opinion Leader).
 * - `betType`: Type of bet.
 * - `betLink`: Link for the bet.
 *
 * @param option - Optional additional options.
 * @param option.article - Optional article information for tracking, including:
 * - `title`: Article title.
 * - `guid`: Unique identifier for the article.
 * - `publish_date`: Article publish date.
 * - `category`: Article category.
 * - `sub_category`: Article sub-category.
 * - `tag`: Article tags.
 * - `segment_codes`: Optional segment codes.
 * - `account_list_codes`: Optional account list codes.
 */
export function nonPageTracker(
    interaction: Partial<InteractionInfo> | PreviewInteractionInfo | any,
    option?: { article?: TrackedArticleInfo },
    isPreviewP?: boolean
) {
    isPreviewP ? previewNonPageTracker(interaction) : _nonPageTracker(interaction, option);
}

function _nonPageTracker(
    interaction: Partial<InteractionInfo>,
    option?: { article?: TrackedArticleInfo }
) {
    if (!window.adobeDataLayer) {
        console.error('tracker not ready:', interaction);
    } else {
        const param: NonPageViewParam = {
            event: 'non-pageview',
            interaction: {
                eventID: '',
                eventValue: '',
                eventType: '',
                preference: '',
                videoURL: '',
                betLink: '',
                betType: '',
                kolName: '',
                raceID: '',
                ...interaction,
            },
            article: {
                category: '',
                guid: '',
                publish_date: '',
                sub_category: '',
                tag: '',
                title: '',
                ...option?.article,
            },
        };
        window.adobeDataLayer.push(param);
    }
}

/** only preview page tracker */
export function previewNonPageTracker(interaction: PreviewInteractionInfo) {
    if (!window.adobeDataLayer) {
        console.error('tracker not ready:', interaction);
    } else {
        const { eventID, title, id, language } = interaction;
        const param: PageViewTrackedParam = {
            // options: pageview / interaction
            event: 'interaction',
            interaction: {
                eventID: eventID || '',
            },
            page: {
                name: '',
                id: 'o2oDiscoverPreview',
                language: language ?? 'en',
            },
            user: {
                cuno: '',
                customer_segment: '',
                tagged_member: '',
                targeted_member: '',
            },
            content: {
                id,
                title,
            },
        };
        window.adobeDataLayer.push(param);
    }
}

export function kolNonPageTracker(
    interaction: Partial<InteractionInfo>,
    option?: { article?: TrackedArticleInfo }
) {
    return _nonPageTracker(interaction, option);
}

export function trackPreferenceStep1(
    selectedStep1Value: DeepReadonly<PreferenceFragment> | undefined,
    eventID = TrackingEvent.PreferenceLevel
) {
    nonPageTracker({
        eventID,
        preference: selectedStep1Value?.waCode?.value?.trim() || selectedStep1Value?.key || 'na',
    });
}

export function trackPreferenceStep2(
    selectedStep2Values: (DeepReadonly<PreferenceFragment> | undefined)[],
    maxLength: number,
    eventID = TrackingEvent.PreferenceInterest
) {
    const step2TrackingData = selectedStep2Values.map(
        (value, idx) => `${idx + 1}:${value?.waCode?.value?.trim() || value?.key}`
    );
    for (let idx = selectedStep2Values.length; idx < maxLength; idx++) {
        step2TrackingData.push(`${idx + 1}:na`);
    }
    nonPageTracker({
        eventID,
        preference: step2TrackingData.join(','),
    });
}

export function trackPreferenceStep3(
    selectedStep3Values: (DeepReadonly<PreferenceFragment> | undefined)[],
    eventID = TrackingEvent.PreferenceJockey
) {
    nonPageTracker({
        eventID,
        preference:
            selectedStep3Values
                .map((prefer) => prefer?.waCode?.value?.trim() || prefer?.key)
                .join(',') || 'na',
    });
}

export function trackPreferenceStep4(
    selectedStep4Values: (DeepReadonly<PreferenceFragment> | undefined)[],
    eventID = TrackingEvent.PreferenceJockey
) {
    nonPageTracker({
        eventID,
        preference:
            selectedStep4Values
                .map((prefer) => prefer?.waCode?.value?.trim() || prefer?.key)
                .join(',') || 'na',
    });
}
